import { useCallback, useEffect, useState } from 'react';

import {
  Avatar,
  Badge,
  Box,
  Chip,
  Dialog,
  DialogContent,
  Fab,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import { useModal } from 'react-modal-hook';
import { useDebouncedCallback } from 'use-debounce';

import { BlockedContactName } from 'components/BlockedContactName';
import DialogHeader from 'components/DialogHeader';
import ModalAddContato from 'components/ModalAddContato';
import SearchBar from 'components/SearchBar';
import * as socketEvents from 'constants/socketEvents';
import { useVirtualizedList } from 'hooks/useVirtualizedList';
import socket from 'services/socket';
import { useAppDispatch, useAppSelector } from 'store';
import { TipoCanalEnum } from 'store/reducers/canais';
import {
  atualizarContatoStatus,
  atualizarFotoContato,
  ContatoInterface,
  contatosSelector,
  ContatoStatusTypes,
  fetchContatosDialogV2,
  resetContatosDialogV2,
} from 'store/reducers/contatos';
import getContatoStatusLabel from 'utils/getContatoStatusLabel';
import getIconTipoCanal from 'utils/getIconTipoCanal';
import { normalizeContato } from 'utils/normalizeContato';

import VirtualizedListContainer from './VirtualizedListContainer';

type ModalListaContatosProps = {
  open: boolean;
  onClose: () => void;
  onSelect?: (contato: ContatoInterface) => void;
  selected?: ContatoInterface;
  filterByType?: TipoCanalEnum;
};

const useStyles = makeStyles(({ spacing }) => ({
  dialogPaper: {
    minHeight: '90%',
  },
  dialogContent: {
    height: '100%',
    position: 'relative',
  },
  fab: {
    position: 'absolute',
    bottom: spacing(2),
    right: spacing(2),
  },
}));

export default function ModalListaContatos(props: ModalListaContatosProps) {
  const { open, onClose, onSelect, selected, filterByType } = props;
  const classes = useStyles();
  const [filter, setFilter] = useState('');
  const dispatch = useAppDispatch();
  const { contatosDialogV2, contatosDialogV2Cursor, loadingContatosDialogV2 } =
    useAppSelector(contatosSelector);

  const handleSelectContato = useDebouncedCallback(
    (contato: ContatoInterface) => {
      if (onSelect) onSelect(contato);
    },
    300,
  );

  const getContatos = useCallback(
    (params?: {
      search?: string;
      contact?: string;
      cursor?: CursorInterface;
      channelType?: TipoCanalEnum;
    }) => {
      if (!filterByType) {
        dispatch(fetchContatosDialogV2(params));
        return;
      }

      dispatch(
        fetchContatosDialogV2({
          ...params,
          channelType: filterByType,
        }),
      );
    },
    [dispatch, filterByType],
  );

  const onLoadNextContatos = useCallback(() => {
    const { afterCursor } = contatosDialogV2Cursor;
    if (afterCursor) {
      getContatos({ search: filter, cursor: { afterCursor } });
    }
  }, [contatosDialogV2Cursor, filter, getContatos]);

  const { parentRef, virtualItems, rowVirtualizer } = useVirtualizedList({
    items: contatosDialogV2,
    hasMore: Boolean(contatosDialogV2Cursor.afterCursor),
    loading: loadingContatosDialogV2,
    estimateSize: 65,
    next: onLoadNextContatos,
  });

  const handleSearch = (value: string) => {
    setFilter(value);
    if (!loadingContatosDialogV2) {
      dispatch(resetContatosDialogV2());
      getContatos({ search: value });
    }
  };

  const handleCancelSearch = () => {
    setFilter('');
    dispatch(resetContatosDialogV2());
    getContatos();
  };

  const [showModalAdd, hideModalAdd] = useModal(({ in: openModal }) => (
    <ModalAddContato
      open={openModal}
      onClose={hideModalAdd}
      onSuccess={handleCancelSearch}
    />
  ));

  useEffect(() => {
    // observa quando o sttaus do contato foi alterado
    const handlerContatoStatus = (data: {
      contatoId: string;
      status: ContatoStatusTypes;
    }) => {
      dispatch(atualizarContatoStatus(data));
    };
    // observa quando a foto do contato foi alterada
    const handlerContatoFoto = (data: { contatoId: string; foto: string }) => {
      dispatch(atualizarFotoContato(data));
    };

    socket.on(socketEvents.ON_CONTATO_STATUS, handlerContatoStatus);
    socket.on(socketEvents.ON_FOTO_CONTATO_ATUALIZADA, handlerContatoFoto);

    return () => {
      socket.off(socketEvents.ON_CONTATO_STATUS, handlerContatoStatus);
      socket.off(socketEvents.ON_FOTO_CONTATO_ATUALIZADA, handlerContatoFoto);
    };
  }, [dispatch]);

  const renderContatos = virtualItems.map(virtualRow => {
    const contato = contatosDialogV2[virtualRow.index];

    if (!contato) return null;

    const {
      id,
      name,
      status,
      contact: numeroContato,
      channelType: tipoCanal,
    } = contato;
    const invalid = status === 'NAO_VERIFICADO' || status === 'INVALIDO';
    const isBlocked = Boolean(contato.blockedAt);
    const IconTipoCanal = getIconTipoCanal({
      tipoCanal,
    });

    const isInstagramOrMessengerChannel =
      tipoCanal === TipoCanalEnum.INSTAGRAM ||
      tipoCanal === TipoCanalEnum.MESSENGER;

    const normalizedContato = normalizeContato(contato);

    return (
      <ListItem
        button
        key={id}
        ref={rowVirtualizer.measureElement}
        data-index={virtualRow.index}
        selected={selected?.id === contato.id}
        onClick={() => {
          handleSelectContato(normalizedContato);
          onClose();
        }}
      >
        <ListItemAvatar>
          <Badge
            overlap="circular"
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            badgeContent={IconTipoCanal}
          >
            <Avatar src={contato.fotoUrl} />
          </Badge>
        </ListItemAvatar>

        <ListItemText
          primary={<BlockedContactName name={name} isBlocked={isBlocked} />}
          secondary={
            isInstagramOrMessengerChannel
              ? `Canal: ${contato.channel?.name}`
              : numeroContato
          }
          primaryTypographyProps={
            {
              noWrap: true,
              variant: 'body2',
              component: 'div',
            } as Record<string, unknown>
          }
          secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
        />

        {invalid ? (
          <Chip
            size="small"
            label={getContatoStatusLabel(status)}
            color="secondary"
            style={{ textTransform: 'lowercase' }}
          />
        ) : null}
      </ListItem>
    );
  });

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      scroll="paper"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      TransitionProps={{
        onEnter: () => {
          getContatos();
        },
        onExited: () => {
          dispatch(resetContatosDialogV2());
          setFilter('');
        },
      }}
    >
      <DialogHeader
        title="Contatos"
        description="Selecione um contato da lista."
        onClose={onClose}
      />

      <Box ml={3} mr={3}>
        <SearchBar
          value={filter}
          onChange={handleSearch}
          onCancelSearch={handleCancelSearch}
        />
      </Box>

      <DialogContent
        id="contatosDialog"
        className={classes.dialogContent}
        ref={parentRef}
      >
        <VirtualizedListContainer
          virtualItems={virtualItems}
          virtualizer={rowVirtualizer}
          loading={loadingContatosDialogV2}
          empty={
            filter
              ? {
                  title: 'Tem certeza que digitou certo !?',
                  description: `Não encontramos ninguém chamado "${filter}"`,
                }
              : {
                  title: 'Ainda não há uma lista de contatos!',
                  description: '"Nenhum contato foi adicionado ainda"',
                }
          }
        >
          {renderContatos}
        </VirtualizedListContainer>
      </DialogContent>

      <Tooltip title="Adicionar">
        <Fab
          size="medium"
          color="primary"
          className={classes.fab}
          onClick={showModalAdd}
        >
          <AddIcon />
        </Fab>
      </Tooltip>
    </Dialog>
  );
}
